import React from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "./Logo";

const NAV_ADMIN = [
  { path: "/app/dashboard",  label: "Dashboard",  icon: "◈" },
  { path: "/app/clients",    label: "Clients",     icon: "◉" },
  { path: "/app/add-client", label: "Add Client",  icon: "⊕" },
  { path: "/app/leads",      label: "Leads",       icon: "◎" },
  { path: "/app/bot-tester", label: "Bot Tester",  icon: "◆" },
  { path: "/app/users",      label: "Users",       icon: "👥" },
  { path: "/app/analytics",  label: "Analytics",   icon: "📈" },
  { path: "/app/handoffs",   label: "Handoffs",    icon: "🤝" },
];

const NAV_CLIENT = [
  { path: "/app/dashboard", label: "Dashboard", icon: "◈" },
  { path: "/app/leads",     label: "Leads",     icon: "◎" },
  { path: "/app/settings",  label: "Settings",  icon: "⚙" },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const NAV = user?.role === "admin" ? NAV_ADMIN : NAV_CLIENT;
  const currentPage = NAV.find(n => location.pathname === n.path)?.label || "DealerBot";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div style={s.shell}>
      <aside style={s.sidebar}>

        {/* ✅ Logo — properly placed */}
        <div style={s.logoWrap}>
          <Logo size={30} showText={true} />
        </div>

        <div style={s.divider} />

        <nav style={s.nav}>
          {NAV.map((item) => (
            <NavLink key={item.path} to={item.path}
              style={({ isActive }) => ({ ...s.navItem, ...(isActive ? s.navActive : {}) })}>
              <span style={s.navIcon}>{item.icon}</span>
              <span>{item.label}</span>
              {location.pathname === item.path && <span style={s.navDot} />}
            </NavLink>
          ))}
        </nav>

        <div style={s.sidebarBottom}>
          <div style={s.userInfo}>
            <div style={s.userAvatar}>
              {user?.role === "admin" ? "👑" : "👤"}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>
                {user?.role === "admin" ? "Admin" : "Client"}
              </div>
              <div style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                {user?.email?.split("@")[0]}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={s.statusDot} />
            <span style={{ color: "var(--text-secondary)", fontSize: 12 }}>System Online</span>
          </div>

          <button onClick={handleLogout} style={s.logoutBtn}>
            ⎋ Logout
          </button>
        </div>
      </aside>

      <main style={s.main}>
        <header style={s.header}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{currentPage}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
              /{location.pathname.replace("/", "")}
            </div>
          </div>
          <div style={s.badge}>
            <span>📱</span><span>WhatsApp SaaS Platform</span>
          </div>
        </header>
        <div style={s.content}><Outlet /></div>
      </main>
    </div>
  );
}

const s = {
  shell: { display: "flex", height: "100vh", overflow: "hidden" },
  sidebar: { width: 220, minWidth: 220, background: "var(--bg-secondary)", borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", padding: "20px 0" },
  logoWrap: { padding: "0 20px 20px" },
  divider: { height: 1, background: "var(--border)", margin: "0 0 16px" },
  nav: { display: "flex", flexDirection: "column", gap: 2, padding: "0 10px", flex: 1 },
  navItem: { display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: "var(--radius)", color: "var(--text-secondary)", textDecoration: "none", fontSize: 13, fontWeight: 500, position: "relative" },
  navActive: { background: "var(--accent-glow)", color: "var(--accent)", borderLeft: "2px solid var(--accent)" },
  navIcon: { fontSize: 14, width: 18, textAlign: "center" },
  navDot: { width: 5, height: 5, background: "var(--accent)", borderRadius: "50%", marginLeft: "auto" },
  sidebarBottom: { padding: "16px 20px 0", borderTop: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 8 },
  userInfo: { display: "flex", alignItems: "center", gap: 8, padding: "8px 0" },
  userAvatar: { width: 32, height: 32, borderRadius: 8, background: "var(--bg-elevated)", border: "1px solid var(--border-light)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 },
  statusDot: { width: 7, height: 7, background: "var(--accent)", borderRadius: "50%", animation: "pulse 2s infinite", boxShadow: "0 0 6px var(--accent)" },
  logoutBtn: { background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius)", color: "var(--text-secondary)", padding: "7px 12px", fontSize: 12, cursor: "pointer", fontFamily: "var(--font-body)", textAlign: "left" },
  main: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
  header: { padding: "18px 28px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--bg-secondary)" },
  badge: { display: "flex", alignItems: "center", gap: 6, background: "var(--accent-glow)", border: "1px solid rgba(37,211,102,0.2)", borderRadius: 20, padding: "4px 12px", fontSize: 12, color: "var(--accent)" },
  content: { flex: 1, overflow: "auto", padding: "28px" },
};